import { Phone, Mail, CheckCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { ListingFormValues } from "@/components/listings/filters/FilterSchema";
import { Checkbox } from "@/components/ui/checkbox";
import { useEffect } from "react"; // Import useEffect
import { useAuth } from "@/context/AuthContext"; // Import useAuth

interface ContactStepProps {
  form: UseFormReturn<ListingFormValues>;
}

const ContactStep = ({ form }: ContactStepProps) => {
  const { user } = useAuth(); // Use the useAuth hook

  useEffect(() => {
    if (user) {
      // Pre-fill contact details from user data if available and form fields are empty
      if (!form.getValues("contactDetails.name") && user.contact_name) {
        form.setValue("contactDetails.name", user.contact_name, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (!form.getValues("contactDetails.phone") && user.contact_phone) {
        form.setValue("contactDetails.phone", user.contact_phone, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (!form.getValues("contactDetails.email") && user.contact_email) {
        form.setValue("contactDetails.email", user.contact_email, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      // Note: contact_botim and hide_contact are listing-specific, not user-specific, so they are not pre-filled from user data.
    }
  }, [user, form]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-propertylk-blue" />
            Contact Information
          </CardTitle>
          <CardDescription>
            Enter your contact details for potential buyers/renters
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="contactDetails.name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Name/Company Name <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Commercial Property LK Private LTD"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="contactDetails.phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Contact Number <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., +94 77 123 4567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactDetails.email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email Address <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., contact@commercialproperty.lk"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="contactDetails.whatsapp"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Available on WhatsApp</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactDetails.botim"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Available on Botim</FormLabel>
                  </div>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="contactDetails.hideContactUntilRequest"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border rounded-md">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    Hide contact details until requested by user
                  </FormLabel>
                  <p className="text-sm text-gray-500">
                    Users will need to request your contact information before
                    viewing it
                  </p>
                </div>
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-propertylk-blue" />
            Terms and Conditions
          </CardTitle>
          <CardDescription>
            Please confirm the following before proceeding
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="authorizedToSell"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border rounded-md">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    I confirm that I have received clear authorization from the
                    owner of the property to act as the agent/broker for this
                    property
                  </FormLabel>
                  <p className="text-sm text-gray-500">
                    I understand that advertising a property without the owner's
                    permission is a criminal offense and can lead to
                    prosecution.
                  </p>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="termsAccepted"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border rounded-md">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    I have read, understood and accept the Terms and Conditions
                    of CommercialProperty.lk
                  </FormLabel>
                  <p className="text-sm text-gray-500">
                    Please review our{" "}
                    <a
                      href="#"
                      className="text-propertylk-blue hover:underline"
                    >
                      Terms and Conditions
                    </a>{" "}
                    before proceeding
                  </p>
                </div>
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactStep;
